import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MIN_PW_LEN = 12;

function strengthIssues(pw) {
  const issues = [];
  if (pw.length < MIN_PW_LEN) issues.push(`minstens ${MIN_PW_LEN} tekens`);
  if (!/[A-Z]/.test(pw)) issues.push('één hoofdletter');
  if (!/[a-z]/.test(pw)) issues.push('één kleine letter');
  if (!/\d/.test(pw)) issues.push('één cijfer');
  return issues;
}

export default function AdminRecoverySetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  const [validating, setValidating] = useState(true);
  const [tokenStatus, setTokenStatus] = useState('unknown'); // valid|expired|used|invalid
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [done, setDone] = useState(false);

  // Robots noindex
  useEffect(() => {
    const prev = document.title;
    document.title = 'Wachtwoord instellen — Powersure (intern)';
    const m = document.createElement('meta');
    m.setAttribute('name', 'robots');
    m.setAttribute('content', 'noindex, nofollow, noarchive');
    m.setAttribute('data-recovery', '1');
    document.head.appendChild(m);
    return () => { document.title = prev; m.remove(); };
  }, []);

  useEffect(() => {
    let ignore = false;
    if (!token) {
      setValidating(false);
      setTokenStatus('invalid');
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/admin/recovery/owner/validate?token=${encodeURIComponent(token)}`);
        const j = await r.json();
        if (!ignore) setTokenStatus(j.status || 'invalid');
      } catch {
        if (!ignore) setTokenStatus('invalid');
      } finally {
        if (!ignore) setValidating(false);
      }
    })();
    return () => { ignore = true; };
  }, [token]);

  const issues = strengthIssues(pw);
  const matches = pw && pw === pw2;
  const canSubmit = tokenStatus === 'valid' && issues.length === 0 && matches && !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setErrorCode('');
    try {
      const r = await fetch(`${API}/admin/recovery/owner/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw, password_confirm: pw2 }),
      });
      const j = await r.json();
      if (j.ok && j.status === 'password_set') {
        setDone(true);
        setTimeout(() => navigate('/admin/login', { replace: true }), 1500);
      } else {
        setErrorCode(j.status || 'unknown_error');
      }
    } catch {
      setErrorCode('network_error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-recovery-page"
      className="min-h-screen text-white flex items-center justify-center px-4"
      style={{ background: 'radial-gradient(circle at 20% 0%, #0c2118 0%, #050b09 60%)' }}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 p-6"
           style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
          <h1 data-testid="admin-recovery-title" className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
            Owner-recovery — wachtwoord instellen
          </h1>
        </div>
        <p className="text-[12.5px] text-white/60 mb-5">
          Stel hieronder een nieuw wachtwoord in voor de Powersure admin-toegang.
          Eenmalige link.
        </p>

        {validating && (
          <p data-testid="admin-recovery-validating" className="text-[13px] text-white/60">Link valideren…</p>
        )}

        {!validating && tokenStatus !== 'valid' && (
          <div data-testid="admin-recovery-token-error" className="rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-200 p-3 text-[12.5px] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">
                {tokenStatus === 'expired' ? 'Link verlopen' :
                 tokenStatus === 'used' ? 'Link al gebruikt' : 'Ongeldige link'}
              </div>
              <p>Vraag een nieuwe owner-recovery aan via de owner-mailbox.</p>
            </div>
          </div>
        )}

        {!validating && tokenStatus === 'valid' && !done && (
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="text-[12.5px] text-white/75">Nieuw wachtwoord</span>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="admin-recovery-pw-input"
                className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400/40"
                autoComplete="new-password"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="text-[12.5px] text-white/75">Bevestig wachtwoord</span>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="admin-recovery-pw-confirm"
                className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400/40"
                autoComplete="new-password"
              />
            </label>

            {pw && (
              <ul data-testid="admin-recovery-pw-checks" className="text-[12px] space-y-1">
                {issues.length === 0 ? (
                  <li className="text-emerald-300 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> wachtwoord voldoet aan eisen
                  </li>
                ) : issues.map((i) => (
                  <li key={i} className="text-amber-300">• {i}</li>
                ))}
                <li className={matches ? 'text-emerald-300' : 'text-white/45'}>
                  • wachtwoorden komen overeen
                </li>
              </ul>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="admin-recovery-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-[#06140e] font-semibold text-sm disabled:opacity-40">
              <Lock className="w-4 h-4" />
              Wachtwoord instellen
            </button>

            {errorCode && (
              <div data-testid="admin-recovery-error" className="rounded-xl border border-rose-400/30 bg-rose-400/10 text-rose-200 p-2 text-[12px]">
                {errorCode === 'password_mismatch' && 'Wachtwoorden komen niet overeen.'}
                {errorCode === 'password_too_short' && `Wachtwoord moet minstens ${MIN_PW_LEN} tekens zijn.`}
                {errorCode === 'password_needs_uppercase' && 'Wachtwoord heeft een hoofdletter nodig.'}
                {errorCode === 'password_needs_lowercase' && 'Wachtwoord heeft een kleine letter nodig.'}
                {errorCode === 'password_needs_digit' && 'Wachtwoord heeft een cijfer nodig.'}
                {errorCode === 'invalid_token' && 'Link ongeldig. Vraag een nieuwe aan.'}
                {errorCode === 'token_already_used' && 'Link is al gebruikt.'}
                {errorCode === 'token_expired' && 'Link is verlopen.'}
                {(!['password_mismatch','password_too_short','password_needs_uppercase','password_needs_lowercase','password_needs_digit','invalid_token','token_already_used','token_expired'].includes(errorCode)) && 'Er ging iets mis. Probeer opnieuw.'}
              </div>
            )}
          </form>
        )}

        {done && (
          <div data-testid="admin-recovery-success" className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 text-emerald-200 p-3 text-[13px] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Wachtwoord ingesteld. Je wordt doorgestuurd naar de login…
          </div>
        )}

        <p className="text-[10.5px] text-white/40 mt-5 leading-relaxed">
          Veiligheidsmaatregel: deze link werkt eenmalig en is gekoppeld aan
          één owner-mailbox. Geen wachtwoord wordt per mail verstuurd.
        </p>
      </div>
    </div>
  );
}
